import { MilestoneTask } from "../../../api/homeSiteMilestones/types";
import { DivisionMileStone } from "../../../api/divisionMileStones/types";
import { MILESTONE_FILTER_VALUES } from "../../../components/milestoneFilterDropdown/types";

export interface MilestoneProgressData {
  progress: number;
  completed: number;
  total: number;
  milestones: Array<{ name: string; completed: number; total: number; progress: number; milestoneOrder: number }>;
}

export interface MilestoneProgressMap {
  [homeSiteId: string]: MilestoneProgressData;
}

/**
 * Calculates milestone progress for each homesite.
 * Mirrors the modal's logic in useMilestoneData + useMilestoneState:
 *   - Division milestones (status !== false) define the skeleton and total task count
 *   - Automated tasks (from division template) are always counted as completed
 *   - Non-automated tasks with status === true are counted as completed
 */
export const calculateMilestoneProgress = (
  allMilestoneTasks: MilestoneTask[] | undefined,
  divisionMilestones: DivisionMileStone[] | undefined,
  selectedMilestone: string | null
): MilestoneProgressMap => {
  if (!allMilestoneTasks || !divisionMilestones) return {};

  const progressMap: MilestoneProgressMap = {};

  // Match modal: include all division milestones where status !== false
  const activeDivTasks = divisionMilestones.filter(m => m.status !== false);
  const activeMilestoneNames = new Set(activeDivTasks.map(m => m.mileStoneName));
  const activeTaskKeys = new Set(activeDivTasks.map(m => `${m.mileStoneName}-${m.taskName}`));

  // Build ordered milestone definitions with task counts from division skeleton
  const milestoneTaskCounts = new Map<string, number>();
  const milestoneOrderMap = new Map<string, number>();
  const automatedTaskKeys = new Set<string>();
  const automatedTasksByMilestone = new Map<string, Set<string>>();

  for (const m of activeDivTasks) {
    milestoneOrderMap.set(m.mileStoneName, m.mileStoneOrder);
    milestoneTaskCounts.set(m.mileStoneName, (milestoneTaskCounts.get(m.mileStoneName) || 0) + 1);
    if (m.automated) {
      automatedTaskKeys.add(`${m.mileStoneName}-${m.taskName}`);
      if (!automatedTasksByMilestone.has(m.mileStoneName)) {
        automatedTasksByMilestone.set(m.mileStoneName, new Set());
      }
      automatedTasksByMilestone.get(m.mileStoneName)!.add(m.taskName);
    }
  }

  const milestoneDefinitions: { name: string; milestoneOrder: number; totalDefinedTasks: number }[] = [];
  for (const [name, count] of milestoneTaskCounts.entries()) {
    milestoneDefinitions.push({
      name,
      milestoneOrder: milestoneOrderMap.get(name) ?? Infinity,
      totalDefinedTasks: count,
    });
  }
  milestoneDefinitions.sort((a, b) => a.milestoneOrder - b.milestoneOrder);

  // Apply milestone filter if selected
  const hasMilestoneFilter = selectedMilestone && selectedMilestone !== MILESTONE_FILTER_VALUES.allMilestones;
  const selectedMilestoneName = hasMilestoneFilter ? selectedMilestone : null;

  const filteredDefinitions = selectedMilestoneName
    ? milestoneDefinitions.filter(d => d.name === selectedMilestoneName)
    : milestoneDefinitions;

  // Group homesite tasks: track all tasks, completed tasks, and task names
  const homeSiteTaskData = new Map<string, Map<string, { all: Set<string>; completed: Set<string>; taskNames: Set<string> }>>();
  const homeSiteIds = new Set<string>();

  for (const task of allMilestoneTasks) {
    if (!activeTaskKeys.has(`${task.mileStoneName}-${task.taskName}`)) continue;
    if (selectedMilestoneName && task.mileStoneName !== selectedMilestoneName) continue;

    homeSiteIds.add(task.homeSiteId);

    if (!homeSiteTaskData.has(task.homeSiteId)) {
      homeSiteTaskData.set(task.homeSiteId, new Map());
    }
    const msMap = homeSiteTaskData.get(task.homeSiteId)!;
    if (!msMap.has(task.mileStoneName)) {
      msMap.set(task.mileStoneName, { all: new Set(), completed: new Set(), taskNames: new Set() });
    }
    const msData = msMap.get(task.mileStoneName)!;
    msData.all.add(task.id);
    msData.taskNames.add(task.taskName);

    // Match modal: automated tasks are always completed; others need status === true or marked as not applicable (status === false with completionDate)
    const isAutomated = automatedTaskKeys.has(`${task.mileStoneName}-${task.taskName}`);
    const isNotApplicable = task.status === false && task.completionDate != null;
    if (isAutomated || task.status === true || isNotApplicable) {
      msData.completed.add(task.id);
    }
  }

  // Calculate progress per homesite
  for (const homeSiteId of homeSiteIds) {
    let totalTasks = 0;
    let completedCount = 0;
    const taskMap = homeSiteTaskData.get(homeSiteId);

    const milestones = filteredDefinitions.map(def => {
      const data = taskMap?.get(def.name);
      const actualCount = data?.all.size ?? 0;
      const actualTaskNames = data?.taskNames ?? new Set<string>();
      const completedFromRecords = data?.completed.size ?? 0;

      // Automated tasks without records should still count as completed
      const automatedSet = automatedTasksByMilestone.get(def.name);
      let missingAutomatedCount = 0;
      if (automatedSet) {
        for (const taskName of automatedSet) {
          if (!actualTaskNames.has(taskName)) {
            missingAutomatedCount++;
          }
        }
      }

      const completed = completedFromRecords + missingAutomatedCount;
      const total = Math.max(actualCount, def.totalDefinedTasks);
      const milestoneProgress = total > 0 ? Math.round((completed / total) * 100) : 0;

      totalTasks += total;
      completedCount += completed;

      return {
        name: def.name,
        completed,
        total,
        progress: milestoneProgress,
        milestoneOrder: def.milestoneOrder,
      };
    });

    const progress = totalTasks > 0
      ? Math.round((completedCount / totalTasks) * 100)
      : 0;

    progressMap[homeSiteId] = {
      progress,
      completed: completedCount,
      total: totalTasks,
      milestones,
    };
  }

  return progressMap;
};
